import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { loginRequest } from '@/config/msalConfig';
import { useAuth } from '@/App';
import SEO from '@/components/SEO';

// Local components
import { TwoFactorSetup } from './TwoFactorSetup';
import { TwoFactorVerify } from './TwoFactorVerify';
import { LoginForm } from './LoginForm';
import { LoadingScreen } from './LoadingScreen';
import { API, msalInstance, isMicrosoftAuthEnabled } from './constants';

export default function LoginPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Basic login state
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  
  // 2FA verification state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const [available2FAMethods, setAvailable2FAMethods] = useState([]);
  const [maskedPhone, setMaskedPhone] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  
  // Mandatory 2FA setup state
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [setupUser, setSetupUser] = useState(null);
  const [setupStep, setSetupStep] = useState('init');
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const redirectPath = user.role === 'admin' ? '/admin' : '/dashboard';
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate]);

  // Handle redirect response from Microsoft login
  useEffect(() => {
    if (user) return;
    handleMicrosoftRedirectResponse();
  }, [navigate, user]);

  const handleMicrosoftRedirectResponse = async () => {
    if (!msalInstance || !isMicrosoftAuthEnabled()) {
      return;
    }
    
    try {
      await msalInstance.initialize();
      const response = await msalInstance.handleRedirectPromise();
      
      if (response) {
        let tokenToSend = response.idToken;
        
        if (!tokenToSend && response.account) {
          try {
            const silentRequest = {
              scopes: ['openid', 'profile', 'email'],
              account: response.account
            };
            const silentResponse = await msalInstance.acquireTokenSilent(silentRequest);
            tokenToSend = silentResponse.idToken;
          } catch (silentError) {
            console.error('[MS Auth] Silent token acquisition failed:', silentError);
          }
        }
        
        if (tokenToSend) {
          setMsLoading(true);
          const backendResponse = await axios.post(`${API}/auth/microsoft/login`, {
            access_token: tokenToSend
          }, { withCredentials: true });

          if (backendResponse.data.success) {
            toast.success('Innlogging vellykket!');
            const userRole = backendResponse.data.user?.role;
            const redirectPath = userRole === 'admin' ? '/admin' : '/dashboard';
            window.location.href = redirectPath;
          }
          setMsLoading(false);
        } else {
          setError('Kunne ikke hente ID-token fra Microsoft. Prøv igjen.');
        }
      }
    } catch (error) {
      console.error('[MS Auth] Error:', error);
      if (error.errorCode === 'post_request_failed') {
        setError('Microsoft-autentisering feilet. Sjekk at redirect URI er konfigurert som SPA i Azure Portal.');
      } else if (error.response?.status === 403) {
        setError(error.response.data.detail || 'Brukeren er ikke registrert i systemet');
      } else if (error.errorMessage) {
        setError(error.errorMessage);
      } else if (error.message) {
        setError(error.message);
      }
      setMsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!msalInstance || !isMicrosoftAuthEnabled()) {
      toast.error('Microsoft-innlogging er ikke konfigurert');
      return;
    }

    setMsLoading(true);
    setError('');

    try {
      await msalInstance.initialize();
      
      try {
        const loginResponse = await msalInstance.loginPopup({
          ...loginRequest,
          prompt: 'select_account'
        });
        
        if (loginResponse) {
          const tokenToSend = loginResponse.idToken;
          
          if (tokenToSend) {
            const backendResponse = await axios.post(`${API}/auth/microsoft/login`, {
              access_token: tokenToSend
            }, { withCredentials: true });

            if (backendResponse.data.success) {
              toast.success('Innlogging vellykket!');
              const userRole = backendResponse.data.user?.role;
              const redirectPath = userRole === 'admin' ? '/admin' : '/dashboard';
              window.location.href = redirectPath;
              return;
            }
          } else {
            setError('Kunne ikke hente ID-token fra Microsoft');
          }
        }
      } catch (popupError) {
        if (popupError.errorCode === 'popup_window_error' || 
            popupError.errorCode === 'user_cancelled' ||
            popupError.errorCode === 'interaction_in_progress') {
          await msalInstance.loginRedirect(loginRequest);
          return;
        }
        throw popupError;
      }
      
    } catch (error) {
      console.error('Microsoft login error:', error);
      
      if (error.response?.status === 403) {
        setError(error.response.data.detail || 'Brukeren er ikke registrert i systemet');
      } else if (error.errorCode === 'user_cancelled') {
        // User cancelled, don't show error
      } else if (error.errorCode === 'post_request_failed') {
        setError('Nettverksfeil under autentisering. Prøv å åpne siden i et nytt vindu eller bruk en annen nettleser.');
      } else {
        setError('Kunne ikke logge inn med Microsoft. Prøv igjen senere.');
      }
      setMsLoading(false);
    }
  };

  const handleLoginResponse = (response, credentials, redirectPath) => {
    // Check if 2FA setup is required (mandatory)
    if (response.data.requires_2fa_setup) {
      setSetupToken(response.data.setup_token);
      setSetupUser(response.data.user);
      setPendingCredentials({ ...credentials, redirectPath });
      setRequires2FASetup(true);
      setSetupStep('init');
      return true;
    }
    
    // Check if 2FA verification is required
    if (response.data.requires_2fa) {
      setPendingCredentials({ ...credentials, redirectPath });
      setPendingUserId(response.data.user_id);
      setAvailable2FAMethods(response.data.available_methods || ['authenticator']);
      setMaskedPhone(response.data.masked_phone || null);
      setRequires2FA(true);
      return true;
    }
    
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError('Vennligst fyll ut alle feltene');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/login`, formData, {
        withCredentials: true
      });

      if (handleLoginResponse(response, formData, response.data.user?.role === 'admin' ? '/admin' : '/dashboard')) {
        setLoading(false);
        return;
      }

      if (response.data.user) {
        toast.success('Velkommen tilbake!');
        const redirectPath = response.data.user.role === 'admin' ? '/admin' : '/dashboard';
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        setError('Feil e-post eller passord');
      } else if (error.response?.status === 429) {
        setError(error.response?.data?.detail || 'For mange forsøk. Vent litt og prøv igjen.');
      } else {
        setError(error.response?.data?.detail || 'Noe gikk galt. Prøv igjen senere.');
      }
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!twoFactorCode || twoFactorCode.replace(/\s/g, '').length < 6) {
      setError('Vennligst skriv inn en gyldig kode');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: pendingCredentials.email,
        password: pendingCredentials.password,
        two_factor_code: twoFactorCode.replace(/\s/g, '')
      }, {
        withCredentials: true
      });

      if (response.data.user) {
        toast.success('Velkommen tilbake!');
        
        const redirectPath = pendingCredentials.redirectPath || 
          (response.data.user.role === 'admin' ? '/admin' : '/dashboard');
        
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      }
    } catch (error) {
      console.error('2FA verification error:', error);
      if (error.response?.status === 401) {
        setError('Ugyldig kode. Prøv igjen.');
      } else {
        setError('Noe gikk galt. Prøv igjen senere.');
      }
      setLoading(false);
    }
  };

  // 2FA Setup handlers
  const handleStartSetup = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/2fa/mandatory-setup`, {
        setup_token: setupToken
      });
      
      setSetupData(response.data);
      setSetupStep('qr');
    } catch (error) {
      console.error('Setup error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke starte 2FA-oppsett');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    setError('');
    setLoading(true);

    if (!verifyCode || verifyCode.replace(/\s/g, '').length < 6) {
      setError('Vennligst skriv inn en gyldig 6-sifret kode');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/2fa/mandatory-verify`, {
        setup_token: setupToken,
        code: verifyCode.replace(/\s/g, '')
      });
      
      setBackupCodes(response.data.backup_codes);
      setSetupStep('backup');
      toast.success('2FA er nå aktivert!');
    } catch (error) {
      console.error('Verify error:', error);
      setError(error.response?.data?.detail || 'Ugyldig kode. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setRequires2FASetup(false);
    setSetupToken(null);
    setSetupUser(null);
    setSetupStep('init');
    setSetupData(null);
    setVerifyCode('');
    setBackupCodes([]);
    toast.info('Logg inn på nytt med 2FA for å fortsette.');
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllBackupCodes = () => {
    const allCodes = backupCodes.join('\n');
    navigator.clipboard.writeText(allCodes);
    setCopiedIndex('all');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const resetTo2FA = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
    setPendingCredentials(null);
    setPendingUserId(null);
    setAvailable2FAMethods([]);
    setMaskedPhone(null);
    setSmsCodeSent(false);
    setError('');
  };

  const resetAll = () => {
    setRequires2FA(false);
    setRequires2FASetup(false);
    setTwoFactorCode('');
    setPendingCredentials(null);
    setPendingUserId(null);
    setAvailable2FAMethods([]);
    setMaskedPhone(null);
    setSmsCodeSent(false);
    setSetupToken(null);
    setSetupUser(null);
    setSetupStep('init');
    setSetupData(null);
    setVerifyCode('');
    setBackupCodes([]);
    setError('');
  };

  const handleSendSmsOtp = async () => {
    if (!pendingUserId) return;
    
    setSendingSms(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/auth/2fa/sms/send`, {
        user_id: pendingUserId
      });
      
      if (response.data.success) {
        setSmsCodeSent(true);
        toast.success(response.data.message || 'SMS-kode sendt!');
      }
    } catch (error) {
      console.error('SMS send error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke sende SMS. Prøv igjen.');
    } finally {
      setSendingSms(false);
    }
  };

  // Show loading while redirecting if user is already logged in
  if (user) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex bg-white">
      <SEO 
        title="Logg inn"
        description="Logg inn på Microsafe kundeportal for å se dine algedata og analyseresultater."
        url="/login"
      />
      
      {/* Left: Image Panel - Padded with rounded corners */}
      <div className="hidden lg:flex lg:w-1/2 p-5">
        <div className="relative w-full h-full rounded-2xl overflow-hidden border border-slate-200">
          <img 
            src="https://images.unsplash.com/photo-1542620489-dd6d51e2b3c9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxub3J3ZWdpYW4lMjBmaXNoJTIwZmFybSUyMG9jZWFufGVufDB8fHx8MTc3MDMwOTQ0M3ww&ixlib=rb-4.1.0&q=85"
            alt="Norsk fiskeoppdrett"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
          <div className="relative z-10 flex flex-col justify-end h-full p-10">
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-white tracking-tight mb-3">
                Profesjonell algeovervåking
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed max-w-md">
                Avansert analyse og varslingssystem for havbruksnæringa. Beskytt fiskehelsen med data.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="mb-0">
          
          {requires2FASetup ? (
            <TwoFactorSetup
              setupStep={setupStep}
              setSetupStep={setSetupStep}
              setupUser={setupUser}
              setupData={setupData}
              verifyCode={verifyCode}
              setVerifyCode={setVerifyCode}
              backupCodes={backupCodes}
              loading={loading}
              error={error}
              copiedIndex={copiedIndex}
              onStartSetup={handleStartSetup}
              onVerifySetup={handleVerifySetup}
              onSetupComplete={handleSetupComplete}
              onCopyToClipboard={copyToClipboard}
              onCopyAllBackupCodes={copyAllBackupCodes}
              onResetAll={resetAll}
            />
          ) : requires2FA ? (
            <TwoFactorVerify
              twoFactorCode={twoFactorCode}
              setTwoFactorCode={setTwoFactorCode}
              available2FAMethods={available2FAMethods}
              maskedPhone={maskedPhone}
              smsCodeSent={smsCodeSent}
              sendingSms={sendingSms}
              loading={loading}
              error={error}
              onSubmit={handle2FASubmit}
              onSendSmsOtp={handleSendSmsOtp}
              onReset={resetTo2FA}
            />
          ) : (
            <LoginForm
              formData={formData}
              setFormData={setFormData}
              loading={loading}
              msLoading={msLoading}
              demoLoading={demoLoading}
              error={error}
              onSubmit={handleSubmit}
              onMicrosoftLogin={handleMicrosoftLogin}
            />
          )}
          </div>

          {/* Back to home link */}
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-slate-700 text-sm font-medium transition-colors"
            >
              Tilbake til forsiden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
